import { LogOut, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { ThemeToggle } from './ThemeToggle';

function initials(s: string | null | undefined): string {
  if (!s) return 'AD';
  const parts = s.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'AD';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function Topbar({ title, subtitle }: { title?: string; subtitle?: string }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <header className="flex h-14 shrink-0 items-center gap-4 border-b border-border bg-surface px-4 md:px-6">
      <div className="min-w-0 flex-1">
        {title && <h1 className="truncate text-sm font-semibold text-text">{title}</h1>}
        {subtitle && <p className="truncate text-xs text-text-muted">{subtitle}</p>}
      </div>

      <div className="flex items-center gap-1">
        <ThemeToggle />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 rounded-md p-1 transition-colors hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-brand-600/40"
            >
              <Avatar className="h-8 w-8">
                <AvatarFallback>{initials(user?.name ?? user?.phone)}</AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-56">
            <DropdownMenuLabel>Signed in as</DropdownMenuLabel>
            <div className="px-2 pb-2">
              <p className="text-sm font-medium text-text">{user?.name ?? 'Admin'}</p>
              <p className="tnum text-xs text-text-muted">{user?.phone}</p>
              <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-brand-700 dark:bg-brand-900/40 dark:text-brand-500">
                <Shield className="h-3 w-3" />
                {user?.role}
              </div>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant="danger"
              onClick={() => logout().then(() => nav('/login'))}
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Mobile signout shortcut so the dropdown isn't required to escape. */}
      <Button
        variant="ghost"
        size="icon"
        className="md:hidden"
        onClick={() => logout().then(() => nav('/login'))}
        aria-label="Sign out"
      >
        <LogOut className="h-4 w-4" />
      </Button>
    </header>
  );
}
